import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const EMPTY = {
  name: "", phone: "", email: "", address: "", property_notes: "", pets: "", access_instructions: "", preferences: "",
  special_requests: "", preferred_frequency: "none", preferred_day: "", preferred_time: "", email_opt_in: false, sms_opt_in: false,
};

const TEXT = [["name", "Full name *"], ["phone", "Phone"], ["email", "Email"], ["address", "Service address"]];
const AREAS = [["property_notes", "Property notes"], ["pets", "Pets"], ["access_instructions", "Access instructions (keys, codes, parking)"], ["preferences", "Cleaning preferences"], ["special_requests", "Special requests"]];

export default function ClientFormDialog({ open, onOpenChange, client, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(client ? { ...EMPTY, ...client, email: client.email || "" } : EMPTY);
      setError("");
    }
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, form[k]]));
    try {
      const { data } = client ? await api.put(`/clients/${client.id}`, payload) : await api.post("/clients", payload);
      toast.success(client ? "Client updated" : "Client added");
      onSaved(data);
      onOpenChange(false);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{client ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {TEXT.map(([k, l]) => (
              <Field key={k} label={l} htmlFor={`c-${k}`} className={k === "address" ? "sm:col-span-2" : ""}>
                <Input id={`c-${k}`} type={k === "email" ? "email" : "text"} required={k === "name"} value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-11" data-testid={`client-${k}-input`} />
              </Field>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {AREAS.map(([k, l]) => (
              <Field key={k} label={l} htmlFor={`c-${k}`}>
                <Textarea id={`c-${k}`} rows={2} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`client-${k}-input`} />
              </Field>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-4 border-t border-slate-100 pt-4">
            <Field label="Preferred frequency">
              <Select value={form.preferred_frequency} onValueChange={(v) => set("preferred_frequency", v)}>
                <SelectTrigger className="h-11" data-testid="client-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["none", "weekly", "biweekly", "monthly", "custom"].map((f) => <SelectItem key={f} value={f}>{f === "none" ? "No preference" : f[0].toUpperCase() + f.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Preferred day" htmlFor="c-day"><Input id="c-day" placeholder="e.g. Tuesday" value={form.preferred_day} onChange={(e) => set("preferred_day", e.target.value)} className="h-11" data-testid="client-preferred-day-input" /></Field>
            <Field label="Preferred time" htmlFor="c-time"><Input id="c-time" placeholder="e.g. Morning" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className="h-11" data-testid="client-preferred-time-input" /></Field>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-3">
            <p className="text-sm font-medium text-slate-900">Message consent</p>
            <p className="text-xs text-slate-500">Only turn these on if the client agreed to receive appointment messages. Changes are logged.</p>
            <label className="flex items-center justify-between gap-3 min-h-[44px]"><span className="text-sm">Email notifications</span><Switch checked={form.email_opt_in} onCheckedChange={(v) => set("email_opt_in", v)} data-testid="client-email-optin-switch" /></label>
            <label className="flex items-center justify-between gap-3 min-h-[44px]"><span className="text-sm">SMS notifications</span><Switch checked={form.sms_opt_in} onCheckedChange={(v) => set("sms_opt_in", v)} data-testid="client-sms-optin-switch" /></label>
          </div>
          {error && <p className="text-sm text-red-700" role="alert" data-testid="client-form-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="client-form-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="client-form-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save client</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
